import React, { useState, useEffect, useMemo } from "react";
import "react-table/react-table.css";
import "styles/component/_similar.scss";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { useHistory } from "react-router-dom";
import { fetchSimilar, openModal } from "redux/actions/global";
import { t, imageX } from "utils";

import { fetchCounterNoticesV3, fetchPendingDenounces } from "redux/actions/counterNotices";
import { Link } from "react-router-dom/cjs/react-router-dom";
import PaginationNew from "components/Pagination/PaginationNew";
import VariantsLoader from "components/Variants/VariantsLoader";
import { PUBLICATION_SELLER_HISTORY } from "components/Modal";

const Similar = ({
  openModal,
  lang,
  article,
  similars,
  fetchSimilar,
  isFetchingSimilars
}) => {
  const history = useHistory();

  const [loading] = useState(false); 
  const [pageSize, setPageSize] = useState(5); 
  const [similarList, setSimilarList] = useState([]);
  const [page, setPage] = useState(0);
  const [from, setFrom] = useState(0); 
  const [pagination, setPagination] = useState(null); 

  const isInternationalPurchase = article?.tags?.includes("cbt_item");
  const CBTCountry = isInternationalPurchase
    ? (article?.seller_address?.search_location?.state?.id === "USA"
      ? "USA"
      : article?.seller_address?.search_location?.state?.name.toUpperCase())
    : undefined;

  useEffect(() => {
    if (article?.catalogProductId) {
      fetchSimilar({ catalogProductId: article.catalogProductId, limit: 5, offset: 0 });
    } else {
      setSimilarList([]);
      setPagination(null);
    }
  
  }, [article?.catalogProductId, pageSize, fetchSimilar]);

  
  useEffect(() => {
    setSimilarList(similars?.similars ?? []);
    setPagination(similars?.pagination ?? null);
    if (similars?.pagination?.limit) setPageSize(similars.pagination.limit);
    if (similars?.pagination) {
      const p = similars.pagination;
      setPage(Math.floor((p.offset || 0) / (p.limit || pageSize)));
      setFrom(0); 
    }
  }, [similars]);


  const paginatedData = useMemo(() => {
    return similarList;
  }, [similarList]);

 
  const totalPages = useMemo(() => {
    const total = pagination?.total || 0;
    const limit = pagination?.limit || pageSize;
    return Math.max(1, Math.ceil(total / limit));
  }, [pagination, pageSize]);

  
  const handlePageChange = (pag) => {
    setPage(pag);
    const limit = pagination?.limit || pageSize;
    const newOffset = pag * limit;

    if (article?.catalogProductId) {
      fetchSimilar({
        catalogProductId: article.catalogProductId,
        limit,
        offset: newOffset
      });
    }
  };

  
  const openPublicationSellerHistory = (e, data, seller) => {
    openModal({
      type: PUBLICATION_SELLER_HISTORY,
      data,
      seller,
      env: "catalog",
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: t("Similar.table.publication"),
        width: 400,
        resizable: false,
        sortable: false,
        Cell: (row) => {
          const imgUrl = row.original?.images?.[0]?.url;
          return (
            <div className="cell-ctn">
              <div className="variantInfo-ctn">
                <div className="variantInfo">
                  <div className="img-container">
                    <div className="img-content">
                      {imgUrl ? <img src={imageX(imgUrl)} alt="" /> : null}
                    </div>
                  </div>
                  <div className="variant-info">
                    <div className="variant-id">{row.original?.id}</div>
                    <div className="variant-title">{row.original?.title}</div>
                    {isInternationalPurchase && (
                      <div className="labelInternational">
                        {t("international_purchase").toUpperCase()} {CBTCountry}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        Header: t("Similar.table.seller"),
        width: 300,
        resizable: false,
        sortable: false,
        Cell: (row) => {
          const sellerId = row.original?.seller?.id;
          const sellerName = row.original?.seller?.nickname ?? "—";
          return (
            <div className="cell-ctn">
              <div className="seller-ctn">
                <div className="seller-name">{sellerName}</div>
                <div>
                  <div
                    className="link_similar"
                    onClick={(e) =>
                      openPublicationSellerHistory(
                        e,
                        {},
                        { id: sellerId }
                      )
                    }
                  >
                    {t("Similar.sellers_history")}
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        Header: t("Similar.table.review"),
        className: "review-header",
        resizable: false,
        sortable: false,
        width: 171,
        Cell: (row) => {
          const id = row.original?.id;
          return (
            <div className="cell-ctn">
              <div className="check-variant-ctn">
                <Link className="check-variant" to={`/article_detail/${id}`}>
                  {t("Similar.check_similar")}
                </Link>
              </div>
            </div>
          );
        },
      },
    ],
    [openModal]
  );

  return (
    <div className="similar-container" id="similar">
      <div className="similar-header-ctn">
        <div className="similar-header-wrapper">
          <div className="similar-header">
            <div className="similar-title">{t("Similar.table.title")}</div>
            <div className="similar-subtitle">
              {t("Similar.table.subtitle")}
            </div>
          </div>
        </div>
      </div>

      <div className="similar-table-ctn">
        {isFetchingSimilars ? (
          <VariantsLoader />
        ) : (
          <>
            <ReactTable
              className="similar-table"
              data={paginatedData}
              columns={columns}
              minRows={0}
              defaultPageSize={pageSize}
              showPagination={false}
              loading={loading}
              loadingText=""
            />
         

            {(pagination?.total || 0) > 5 && (
              <div
                className="link_similar"
                onClick={() => history.push(`/similars/${article?.id}`)}
              >
                {t("Similar.see_alls")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  lang: state.global.lang,
  menus: state.global.menu,
  counterNotices: state.counterNotices,
  pendingDenounces: state.pendingDenounces,
  similars: state.global.similars,             
  isFetchingSimilars: state.global.isFetchingSimilars,
});

const actions = {
  openModal,
  fetchCounterNoticesV3,
  fetchPendingDenounces,
  fetchSimilar,
};

export default connect(mapStateToProps, actions)(Similar);
